// normalized, as advised in https://redux.js.org/usage/structuring-reducers/normalizing-state-shape

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Api from "../api/api";
import { addEntityFetchReducers, EntityState } from "./entity";
import { logOut } from "./globalSlice";
import { RootState } from "./store";
import { licenseCollectionThunk, licenseThunk } from "./licensesSlice";
import { claimThunk, codeClaimsThunk, skuClaimsThunk } from "./claimsSlice";
import { skuThunk } from "./skusSlice";
import { activePlansThunk } from "./plansSlice";
import { subscriptionThunk, switchSubscriptionThunk } from "./subscriptionSlice";
import { useFetchableState } from "./fetchableState";
import { Product } from "../api/models";
import { redeemCodeThunk, redeemResellerCodeThunk, redemptionsThunk, redemptionThunk } from "./redemptionsSlice";
import { storeSkusThunk, storeSkuThunk } from "./storeSlice";

interface ProductsState extends EntityState<Product> {
    debug: string
}

const initialState: ProductsState = {
    entities: {},
    debug: "products state"
}

export const productsSlice = createSlice({
    name: 'products',
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        addEntityFetchReducers<
            Product,
            { product: Product },
            { id: string, accessToken: string }
        >(
            builder,
            (payload) => payload.product,
            productFetchThunk
        )

        builder.addCase(licenseThunk.fulfilled, (state, action) => {
            const { product } = action.payload
            if (product) {
                state.entities[product.id] = {
                    data: product,
                    lastFetched: Date.now()
                }
            }
        })
        builder.addCase(licenseCollectionThunk.fulfilled, (state, action) => {
            const { products } = action.payload
            products.forEach(product => {
                state.entities[product.id] = {
                    data: product,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(activePlansThunk.fulfilled, (state, action) => {
            const { products } = action.payload
            products.forEach(product => {
                state.entities[product.id] = {
                    data: product,
                    lastFetched: Date.now()
                }
            })
        })

        builder.addCase(storeSkuThunk.fulfilled, (state, action) => {
            const { products } = action.payload
            products.forEach(product => {
                state.entities[product.id] = {
                    data: product,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(storeSkusThunk.fulfilled, (state, action) => {
            const { products } = action.payload
            products.forEach(product => {
                state.entities[product.id] = {
                    data: product,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(skuThunk.fulfilled, (state, action) => {
            const { products } = action.payload
            products.forEach(product => {
                state.entities[product.id] = {
                    data: product,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(subscriptionThunk.fulfilled, (state, action) => {
            const { products } = action.payload
            products.forEach(product => {
                state.entities[product.id] = {
                    data: product,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(switchSubscriptionThunk.fulfilled, (state, action) => {
            const { products } = action.payload
            products.forEach(product => {
                state.entities[product.id] = {
                    data: product,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(claimThunk.fulfilled, (state, action) => {
            const { product } = action.payload
            if (product) {
                state.entities[product.id] = {
                    data: product,
                    lastFetched: Date.now()
                }
            }
        })
        builder.addCase(codeClaimsThunk.fulfilled, (state, action) => {
            const { products } = action.payload
            products.forEach(product => {
                state.entities[product.id] = {
                    data: product,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(skuClaimsThunk.fulfilled, (state, action) => {
            const { products } = action.payload
            products.forEach(product => {
                state.entities[product.id] = {
                    data: product,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(redemptionThunk.fulfilled, (state, action) => {
            const { products } = action.payload
            products.forEach(product => {
                state.entities[product.id] = {
                    data: product,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(redemptionsThunk.fulfilled, (state, action) => {
            const { products } = action.payload
            products.forEach(product => {
                state.entities[product.id] = {
                    data: product,
                    lastFetched: Date.now()
                }
            })
        })
        // a code redemption and a reseller-code redemption side-load the same entities
        const onCodeRedeemed = (state: ProductsState, action: { payload: { products: Product[] } }) => {
            const { products } = action.payload
            products.forEach(product => {
                state.entities[product.id] = {
                    data: product,
                    lastFetched: Date.now()
                }
            })
        }
        builder.addCase(redeemCodeThunk.fulfilled, onCodeRedeemed)
        builder.addCase(redeemResellerCodeThunk.fulfilled, onCodeRedeemed)
        builder.addCase(logOut, (state: ProductsState) => {
            state = initialState;
        })
    }
})

/**
 * GET /products/:productId => products.entities[productId].data
 */
export const productFetchThunk = createAsyncThunk('products/fetchProduct', async ({ id, accessToken }: { id: string, accessToken: string }, { getState, requestId }) => {
    const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
    const entity = state.products.entities[id];
    if (entity?.currentRequestId !== requestId)
        throw new Error("Request outdated");
    return await Api.getProduct({ productId: id, accessToken })
})

export function useProduct(id: string, accessToken: string) {
    return useFetchableState<Product>({
        selector: (state: RootState) => state.products.entities[id],
        action: productFetchThunk({ id, accessToken })
    })
}


export default productsSlice.reducer